import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { getInitials } from '@/lib';
import type { Course } from '@/types/api/course.types';
import { ArrowLeft, CheckCheck, ImageUp, Pencil, Settings2, Trash2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ReactNode } from 'react';

interface WorkspaceMode {
  id: string;
  icon: LucideIcon;
  label: string;
}

interface CourseDetailHeaderProps {
  course?: Course;
  workspaceModes: ReadonlyArray<WorkspaceMode>;
  activeMode?: string;
  onModeChange?: (modeId: string) => void;
  onBack: () => void;
  canEdit?: boolean;
  onPublish?: () => void;
  isPublishing?: boolean;
  onEdit?: () => void;
  onUpdateThumbnail?: () => void;
  onManageGradingScheme?: () => void;
  gradingSchemeActionLabel?: string;
  onDelete?: () => void;
  isDeleting?: boolean;
  hideTopControls?: boolean;
  rightAction?: ReactNode;
}

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString();
};

const CourseDetailHeader = ({
  course,
  workspaceModes,
  activeMode,
  onModeChange,
  onBack,
  canEdit = false,
  onPublish,
  isPublishing = false,
  onEdit,
  onUpdateThumbnail,
  onManageGradingScheme,
  gradingSchemeActionLabel = 'Add grading scheme',
  onDelete,
  isDeleting = false,
  hideTopControls = false,
  rightAction,
}: CourseDetailHeaderProps) => {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
            {course?.course_name}
          </h1>
          <div className="mt-2 flex items-center gap-3 text-sm text-muted-foreground">
            <div className="flex items-center gap-2 min-w-0">
              <Avatar className="h-7 w-7">
                <AvatarImage
                  src={course?.lecturer?.photo_url || undefined}
                  alt={course?.lecturer_full_name || 'Instructor'}
                />
                <AvatarFallback>
                  {getInitials(
                    course?.lecturer?.first_name || '',
                    course?.lecturer?.last_name || ''
                  )}
                </AvatarFallback>
              </Avatar>
              <span className="truncate">{course?.lecturer_full_name}</span>
            </div>
            <span aria-hidden>|</span>
            <span>Updated on: {formatDate(course?.updated_date)}</span>
          </div>
        </div>
        {!hideTopControls && (
          <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:justify-end">
            <Button variant="outline" className="shrink-0" onClick={onBack}>
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            {canEdit && course && !course.active && onPublish && (
              <Button
                variant="default"
                className="shrink-0"
                onClick={onPublish}
                disabled={isPublishing}
              >
                <CheckCheck className="h-4 w-4" />
                <span className="hidden sm:inline">Publish</span>
              </Button>
            )}
            {canEdit && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="shrink-0">
                    <Settings2 className="h-4 w-4" />
                    <span className="sr-only">Course settings</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onEdit}>
                    <Pencil className="h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onUpdateThumbnail}>
                    <ImageUp className="h-4 w-4" />
                    Update thumbnail
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onManageGradingScheme}>
                    <Pencil className="h-4 w-4" />
                    {gradingSchemeActionLabel}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={onDelete}
                    disabled={isDeleting}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            {rightAction}
            {workspaceModes.length > 0 && (
              <div className="max-w-full overflow-x-auto rounded-full border bg-muted/40 p-1 order-last md:order-none basis-full md:basis-auto">
                <div className="flex w-max items-center gap-1">
                  {workspaceModes.map((mode) => {
                    const Icon = mode.icon;
                    return (
                      <button
                        key={mode.id}
                        type="button"
                        title={mode.label}
                        onClick={() => onModeChange?.(mode.id)}
                        className={`h-8 w-8 inline-flex shrink-0 items-center justify-center rounded-full border transition-colors ${activeMode === mode.id
                          ? 'border-primary/40 bg-background text-foreground'
                          : 'border-transparent text-muted-foreground hover:bg-background hover:text-foreground'
                          }`}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseDetailHeader;
